import React from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Prompts from './pages/Prompts';
import Playground from './pages/Playground';
import MyImages from './pages/MyImages';
import Videos from './pages/Videos';
import Books from './pages/Books';
import WatermarkRemover from './pages/WatermarkRemover';
import NotePad from './pages/NotePad';
import PdfImageConverter from './pages/PdfImageConverter';
import Credits from './pages/Credits';
import Profile from './pages/Profile';
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageUsers from './pages/admin/ManageUsers';
import ManagePrompts from './pages/admin/ManagePrompts';
import ManageVideos from './pages/admin/ManageVideos';
import ManageBooks from './pages/admin/ManageBooks';
import ManagePackages from './pages/admin/ManagePackages';
import AdminPayments from './pages/admin/AdminPayments';

// Loading Component
const LoadingScreen = () => (
  <div className="min-h-screen bg-[#0f0f15] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-gray-400">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppContent() {
  const { logout } = useAuth();

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        
        {/* Protected Routes */}
        <Route path="/*" element={
          <ProtectedRoute>
            <Layout onLogout={logout}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/prompts" element={<Prompts />} />
                <Route path="/playground" element={<Playground />} />
                <Route path="/images" element={<MyImages />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/books" element={<Books />} />
                <Route path="/watermark" element={<WatermarkRemover />} />
                <Route path="/notepad" element={<NotePad />} />
                <Route path="/converter" element={<PdfImageConverter />} />
                <Route path="/credits" element={<Credits />} />
                <Route path="/profile" element={<Profile />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><ManageUsers /></AdminRoute>} />
                <Route path="/admin/prompts" element={<AdminRoute><ManagePrompts /></AdminRoute>} />
                <Route path="/admin/videos" element={<AdminRoute><ManageVideos /></AdminRoute>} />
                <Route path="/admin/books" element={<AdminRoute><ManageBooks /></AdminRoute>} />
                <Route path="/admin/packages" element={<AdminRoute><ManagePackages /></AdminRoute>} />
                <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
                
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </div>
  );
}

export default App;
